import React, { useState, useEffect, useCallback } from 'react';
import AdminLayout from './AdminLayout';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Switch } from '../../components/ui/switch';
import { Badge } from '../../components/ui/badge';
import { toast } from 'sonner';
import {
  Sparkles, Plus, X, Save, Loader2, Trash2, Edit, Eye, EyeOff,
  Calendar, Tag, Link as LinkIcon, ImageIcon, ArrowUpDown, ArrowUp, ArrowDown,
  Upload,
} from 'lucide-react';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const fmtEUR = (n) => `${Number(n || 0).toLocaleString('fr-FR', { minimumFractionDigits: 0, maximumFractionDigits: 0 })} €`;

const emptyForm = () => ({
  title: '',
  subtitle: '',
  date_start: '',
  date_end: '',
  cabins_remaining: 1,
  places_remaining: 2,
  original_price: 0,
  discounted_price: 0,
  price_label: 'à partir de',
  link_url: '/croisieres',
  image_url: '',
  badge_text: 'Place rare',
  is_active: true,
  sort_order: 0,
});

const LastMinuteForm = ({ open, onClose, onSaved, initial, token }) => {
  const [data, setData] = useState(emptyForm());
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = React.useRef(null);

  useEffect(() => {
    if (open) {
      setData(initial ? { ...emptyForm(), ...initial } : emptyForm());
    }
  }, [open, initial]);

  const upd = (k, v) => setData((d) => ({ ...d, [k]: v }));
  const updNum = (k, v) => setData((d) => ({ ...d, [k]: Number(v) || 0 }));

  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!['image/jpeg', 'image/png', 'image/webp'].includes(file.type)) {
      toast.error('Format non supporté (JPG, PNG ou WebP uniquement)');
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      toast.error('Fichier trop volumineux (max 10 Mo)');
      return;
    }
    setUploading(true);
    try {
      const form = new FormData();
      form.append('file', file);
      const r = await axios.post(`${API}/cruises/upload-image`, form, {
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' },
      });
      upd('image_url', r.data.url);
      toast.success('Image téléchargée');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Erreur upload');
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const submit = async () => {
    if (!data.title.trim()) return toast.error('Titre requis');
    setSaving(true);
    try {
      const hdr = { Authorization: `Bearer ${token}` };
      if (initial?.id) {
        await axios.put(`${API}/admin/last-minute/${initial.id}`, data, { headers: hdr });
        toast.success('Mis à jour');
      } else {
        await axios.post(`${API}/admin/last-minute`, data, { headers: hdr });
        toast.success('Créé');
      }
      onSaved();
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erreur enregistrement');
    } finally { setSaving(false); }
  };

  if (!open) return null;
  const hasDiscount = Number(data.discounted_price) > 0 && Number(data.original_price) > Number(data.discounted_price);
  const discountPct = hasDiscount ? Math.round((1 - data.discounted_price / data.original_price) * 100) : 0;

  return (
    <div className="fixed inset-0 z-[80] bg-black/55 backdrop-blur-sm flex items-end sm:items-center justify-center p-2 sm:p-6" data-testid="last-minute-form-modal">
      <div className="bg-white w-full max-w-3xl max-h-[95vh] overflow-y-auto rounded-2xl shadow-2xl">
        <div className="sticky top-0 z-10 bg-white border-b px-5 sm:px-7 py-4 flex items-center justify-between rounded-t-2xl">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-amber-100 flex items-center justify-center">
              <Sparkles className="h-5 w-5 text-amber-600" />
            </div>
            <div>
              <h2 className="font-display text-lg font-bold text-[#0A2540]">
                {initial ? 'Modifier le départ' : 'Nouveau départ "Dernière minute"'}
              </h2>
              <p className="text-xs text-slate-500">Apparaît sur le bandeau "Place rare" en page d'accueil</p>
            </div>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-slate-100 rounded-lg" data-testid="lm-form-close">
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="px-5 sm:px-7 py-5 space-y-5">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div className="sm:col-span-2">
              <Label>Titre principal *</Label>
              <Input value={data.title} onChange={(e) => upd('title', e.target.value)} placeholder="L'Inoubliable Tour de Corse" data-testid="lm-title-input" />
            </div>
            <div className="sm:col-span-2">
              <Label>Sous-titre <span className="text-[10px] text-slate-400 font-normal">(facultatif)</span></Label>
              <Input value={data.subtitle} onChange={(e) => upd('subtitle', e.target.value)} placeholder="Ajaccio → Bonifacio → Calanches de Piana" />
            </div>
            <div>
              <Label className="flex items-center gap-1.5"><Calendar className="h-3.5 w-3.5" /> Date départ</Label>
              <Input type="date" value={data.date_start || ''} onChange={(e) => upd('date_start', e.target.value)} />
            </div>
            <div>
              <Label className="flex items-center gap-1.5"><Calendar className="h-3.5 w-3.5" /> Date retour</Label>
              <Input type="date" value={data.date_end || ''} onChange={(e) => upd('date_end', e.target.value)} />
            </div>
            <div>
              <Label>Cabines restantes</Label>
              <Input type="number" min="0" value={data.cabins_remaining} onChange={(e) => updNum('cabins_remaining', e.target.value)} data-testid="lm-cabins-input" />
            </div>
            <div>
              <Label>Places restantes <span className="text-[10px] text-slate-400 font-normal">(passagers)</span></Label>
              <Input type="number" min="0" value={data.places_remaining} onChange={(e) => updNum('places_remaining', e.target.value)} />
            </div>
          </div>

          {/* PRIX */}
          <section className="rounded-xl border bg-slate-50/60 p-4">
            <h3 className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500 flex items-center gap-2 mb-3">
              <Tag className="h-3.5 w-3.5" /> Tarification (facultatif)
            </h3>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <div>
                <Label>Libellé</Label>
                <Input value={data.price_label} onChange={(e) => upd('price_label', e.target.value)} placeholder="à partir de" />
              </div>
              <div>
                <Label>Prix initial (€)</Label>
                <Input type="number" min="0" step="50" value={data.original_price} onChange={(e) => updNum('original_price', e.target.value)} placeholder="3500" />
              </div>
              <div>
                <Label>Prix remisé (€) <span className="text-[10px] text-amber-600 font-normal">(0 = pas de remise)</span></Label>
                <Input type="number" min="0" step="50" value={data.discounted_price} onChange={(e) => updNum('discounted_price', e.target.value)} placeholder="2800" />
              </div>
            </div>
            {hasDiscount && (
              <div className="mt-3 text-sm">
                <Badge className="bg-rose-100 text-rose-700 border-rose-300">−{discountPct}% de remise</Badge>
                <span className="ml-3 text-slate-600">
                  <span className="line-through text-slate-400">{fmtEUR(data.original_price)}</span>
                  {' '}<strong className="text-[#0A2540]">{fmtEUR(data.discounted_price)}</strong>
                </span>
              </div>
            )}
          </section>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <Label className="flex items-center gap-1.5"><Tag className="h-3.5 w-3.5" /> Badge</Label>
              <Input value={data.badge_text} onChange={(e) => upd('badge_text', e.target.value)} placeholder="Place rare" />
              <p className="text-[10px] text-slate-400 mt-1">Ex : "Place rare", "Dernière minute", "Offre exclusive"</p>
            </div>
            <div>
              <Label className="flex items-center gap-1.5"><LinkIcon className="h-3.5 w-3.5" /> Lien (URL)</Label>
              <Input value={data.link_url} onChange={(e) => upd('link_url', e.target.value)} placeholder="/croisieres/tour-de-corse" />
            </div>
            <div className="sm:col-span-2">
              <Label className="flex items-center gap-1.5"><ImageIcon className="h-3.5 w-3.5" /> Image <span className="text-[10px] text-slate-400 font-normal">(facultatif — JPG/PNG/WebP, max 10 Mo)</span></Label>
              <div className="mt-1 flex flex-wrap items-stretch gap-3">
                {/* Preview */}
                {data.image_url ? (
                  <div className="relative w-32 h-24 rounded-lg overflow-hidden border bg-slate-100 shrink-0">
                    <img src={data.image_url} alt="Aperçu" className="w-full h-full object-cover" />
                    <button
                      type="button"
                      onClick={() => upd('image_url', '')}
                      className="absolute top-1 right-1 bg-rose-500 hover:bg-rose-600 text-white rounded-full w-6 h-6 flex items-center justify-center shadow-md"
                      title="Retirer l'image"
                      data-testid="lm-image-remove"
                    >
                      <X className="h-3.5 w-3.5" />
                    </button>
                  </div>
                ) : null}
                <div className="flex-1 min-w-[200px] flex flex-col gap-2">
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/jpeg,image/png,image/webp"
                    onChange={handleFileUpload}
                    className="hidden"
                    data-testid="lm-image-file-input"
                  />
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => fileInputRef.current?.click()}
                    disabled={uploading}
                    className="justify-start h-10 border-dashed border-slate-300 text-slate-600 hover:border-[#C4724F] hover:text-[#C4724F] hover:bg-orange-50/50"
                    data-testid="lm-image-upload-btn"
                  >
                    {uploading ? (
                      <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Téléchargement…</>
                    ) : (
                      <><Upload className="h-4 w-4 mr-2" /> {data.image_url ? 'Remplacer l\'image' : 'Choisir une image'}</>
                    )}
                  </Button>
                  <details className="text-[11px] text-slate-400">
                    <summary className="cursor-pointer hover:text-slate-600">…ou coller une URL</summary>
                    <Input
                      value={data.image_url}
                      onChange={(e) => upd('image_url', e.target.value)}
                      placeholder="https://…"
                      className="mt-1.5 h-8 text-xs"
                    />
                  </details>
                </div>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3 items-center bg-slate-50 rounded-xl p-3 border">
            <div>
              <Label>Ordre d'affichage</Label>
              <Input type="number" value={data.sort_order} onChange={(e) => updNum('sort_order', e.target.value)} />
              <p className="text-[10px] text-slate-400 mt-1">Plus petit = affiché en premier</p>
            </div>
            <div className="flex items-center justify-end gap-3">
              <Label htmlFor="lm-active" className="cursor-pointer">{data.is_active ? 'Actif' : 'Désactivé'}</Label>
              <Switch id="lm-active" checked={data.is_active} onCheckedChange={(v) => upd('is_active', v)} data-testid="lm-active-switch" />
            </div>
          </div>
        </div>

        <div className="sticky bottom-0 bg-white border-t px-5 sm:px-7 py-3 flex items-center justify-end gap-2 rounded-b-2xl">
          <Button variant="outline" onClick={onClose}>Annuler</Button>
          <Button onClick={submit} disabled={saving} className="bg-[#C4724F] hover:bg-[#A0552F] text-white" data-testid="lm-save-btn">
            {saving ? <Loader2 className="h-4 w-4 animate-spin mr-1.5" /> : <Save className="h-4 w-4 mr-1.5" />}
            {initial ? 'Mettre à jour' : 'Créer'}
          </Button>
        </div>
      </div>
    </div>
  );
};

const AdminLastMinute = () => {
  const { token } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState({ open: false, initial: null });

  const fetchAll = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    try {
      const r = await axios.get(`${API}/admin/last-minute`, { headers: { Authorization: `Bearer ${token}` } });
      setItems(r.data || []);
    } catch (e) {
      toast.error('Erreur de chargement');
    } finally { setLoading(false); }
  }, [token]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const toggleActive = async (item) => {
    try {
      await axios.put(`${API}/admin/last-minute/${item.id}`, { is_active: !item.is_active }, { headers: { Authorization: `Bearer ${token}` } });
      fetchAll();
    } catch (e) {
      toast.error('Erreur');
    }
  };

  const handleDelete = async (item) => {
    if (!window.confirm(`Supprimer "${item.title}" ?`)) return;
    try {
      await axios.delete(`${API}/admin/last-minute/${item.id}`, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Supprimé');
      fetchAll();
    } catch (e) {
      toast.error('Erreur suppression');
    }
  };

  const moveSort = async (item, delta) => {
    try {
      await axios.put(`${API}/admin/last-minute/${item.id}`, { sort_order: (item.sort_order || 0) + delta }, { headers: { Authorization: `Bearer ${token}` } });
      fetchAll();
    } catch (e) {
      toast.error('Erreur');
    }
  };

  return (
    <AdminLayout title="Dernière minute">
      <div className="space-y-5" data-testid="admin-last-minute">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <h1 className="font-display text-2xl sm:text-3xl font-bold text-[#0A2540] flex items-center gap-2">
              <Sparkles className="h-6 w-6 text-amber-500" />
              Dernière minute
            </h1>
            <p className="text-sm text-slate-500 mt-1">Mettez en avant des départs avec places restantes (avec ou sans remise). Affichage automatique sur la page d'accueil.</p>
          </div>
          <Button onClick={() => setModal({ open: true, initial: null })} className="bg-[#C4724F] hover:bg-[#A0552F] text-white" data-testid="new-lm-btn">
            <Plus className="h-4 w-4 mr-1.5" /> Nouveau départ
          </Button>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-20 text-slate-400"><Loader2 className="h-6 w-6 animate-spin" /></div>
        ) : items.length === 0 ? (
          <Card><CardContent className="p-10 text-center text-slate-500">
            <Sparkles className="h-10 w-10 mx-auto mb-3 text-amber-300" />
            <p className="text-sm">Aucun départ "Dernière minute" pour le moment.</p>
            <Button onClick={() => setModal({ open: true, initial: null })} className="mt-4 bg-[#C4724F] hover:bg-[#A0552F] text-white">
              <Plus className="h-4 w-4 mr-1.5" /> Créer le premier
            </Button>
          </CardContent></Card>
        ) : (
          <div className="space-y-3">
            {items.map((item) => {
              const hasDiscount = Number(item.discounted_price) > 0 && Number(item.original_price) > Number(item.discounted_price);
              const discountPct = hasDiscount ? Math.round((1 - item.discounted_price / item.original_price) * 100) : 0;
              return (
                <Card key={item.id} className={`overflow-hidden ${!item.is_active ? 'opacity-60' : ''}`} data-testid={`lm-card-${item.id}`}>
                  <CardContent className="p-4 flex flex-wrap items-center gap-4">
                    <div className="flex flex-col gap-1">
                      <button onClick={() => moveSort(item, -1)} className="p-1 hover:bg-slate-100 rounded text-slate-400 hover:text-slate-700"><ArrowUp className="h-3.5 w-3.5" /></button>
                      <span className="text-[10px] text-center text-slate-400 font-mono">{item.sort_order}</span>
                      <button onClick={() => moveSort(item, 1)} className="p-1 hover:bg-slate-100 rounded text-slate-400 hover:text-slate-700"><ArrowDown className="h-3.5 w-3.5" /></button>
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex flex-wrap items-center gap-2 mb-1">
                        <Badge className="bg-amber-100 text-amber-800 border-amber-300 text-[10px]">{item.badge_text}</Badge>
                        {!item.is_active && <Badge className="bg-slate-200 text-slate-600 text-[10px]">Désactivé</Badge>}
                      </div>
                      <div className="font-display font-bold text-[#0A2540] truncate">{item.title}</div>
                      <div className="text-xs text-slate-500 truncate">{item.subtitle}</div>
                      <div className="text-xs text-slate-500 mt-1">
                        {item.date_start} → {item.date_end}
                        <span className="mx-2">·</span>
                        {item.cabins_remaining} cabine{item.cabins_remaining > 1 ? 's' : ''} ({item.places_remaining} place{item.places_remaining > 1 ? 's' : ''})
                      </div>
                    </div>
                    <div className="text-right shrink-0">
                      {hasDiscount ? (
                        <>
                          <div className="text-[10px] text-slate-400 line-through">{fmtEUR(item.original_price)}</div>
                          <div className="text-lg font-bold text-rose-600">{fmtEUR(item.discounted_price)}</div>
                          <Badge className="bg-rose-100 text-rose-700 border-rose-300 text-[10px]">−{discountPct}%</Badge>
                        </>
                      ) : item.original_price > 0 ? (
                        <div className="text-lg font-bold text-[#0A2540]">{fmtEUR(item.original_price)}</div>
                      ) : (
                        <div className="text-xs text-slate-400 italic">Sans tarif</div>
                      )}
                    </div>
                    <div className="flex items-center gap-1.5">
                      <Button size="sm" variant="outline" onClick={() => toggleActive(item)} className="h-8 text-xs" title={item.is_active ? 'Masquer' : 'Afficher'}>
                        {item.is_active ? <Eye className="h-3.5 w-3.5" /> : <EyeOff className="h-3.5 w-3.5" />}
                      </Button>
                      <Button size="sm" variant="outline" onClick={() => setModal({ open: true, initial: item })} className="h-8 text-xs" data-testid={`edit-lm-${item.id}`}>
                        <Edit className="h-3.5 w-3.5" />
                      </Button>
                      <Button size="sm" variant="outline" onClick={() => handleDelete(item)} className="h-8 text-xs border-rose-300 text-rose-600 hover:bg-rose-50">
                        <Trash2 className="h-3.5 w-3.5" />
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}
      </div>

      <LastMinuteForm
        open={modal.open}
        onClose={() => setModal({ open: false, initial: null })}
        onSaved={fetchAll}
        initial={modal.initial}
        token={token}
      />
    </AdminLayout>
  );
};

export default AdminLastMinute;
